import React from 'react';
import Modal from 'components/common/Modal/modal';
import useLocalize from 'components/common/Localization/useLocalize.hook';
import { WarningIconNew } from 'components/common/svg';
import SecondaryButton from 'components/common/SecondaryButton/secondary-button.component';
import PrimaryButton from 'components/common/PrimaryButton/primary-button.component';
import { PendoClassNames } from 'components/feature/Report/ReportSidebar/common/pendoClassNames';
import StyledDeleteConfirmationModal from './delete-confirmation-modal.styled';

interface IConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onDeleteConfirmation: () => void;
  modalContent: React.ReactNode;
  optionalContent?: React.ReactNode;
  // Optional config (defaults preserve the original look for existing consumers):
  titleIcon?: React.ReactNode; // header icon — defaults to the warning triangle
  confirmationQuestion?: React.ReactNode; // extra line rendered below optionalContent
  // true => confirm is a primary button on the RIGHT, cancel a secondary on the LEFT (the confirm
  // button gets the `se-danger-confirm` class; its colour — red vs default blue — is decided by the
  // consuming wrapper's styling). false/omitted keeps the original confirm-left / cancel-right order.
  danger?: boolean;
  confirmLabel?: string; // confirm button text — defaults to "Yes, Delete"
  cancelLabel?: string; // cancel button text — defaults to "No"
}
const DeleteConfirmationModal = ({
  show,
  onClose,
  onDeleteConfirmation,
  modalContent,
  optionalContent,
  titleIcon,
  confirmationQuestion,
  danger,
  confirmLabel,
  cancelLabel,
}: IConfirmationModalProps) => {
  const confirmText = confirmLabel || `${useLocalize('cmn.deleteConfirmText')}`;
  const cancelText = cancelLabel || `${useLocalize('cmn.no')}`;
  const titleContent = (
    <div className="se-confirm-delete-title-container">
      {titleIcon || <WarningIconNew />}
      <div className="se-confirm-title">{useLocalize('cmn.warning')}</div>
    </div>
  );
  const confirmButton = (
    <SecondaryButton
      uniqueKey="se-confirm-delete-button"
      onClickHandler={() => onDeleteConfirmation()}
      displayName={confirmText}
      classNames={['se-confirm-delete-button', PendoClassNames.ExpDeleteConfirmBtn]}
      tooltip={confirmText}
      tooltipPlacement="bottom"
    />
  );
  const cancelButton = (
    <PrimaryButton
      uniqueKey="se-cancel-delete-button"
      isDisable={false}
      displayName={cancelText}
      tooltipOverlay={<span>{cancelText}</span>}
      onClickHandler={() => onClose()}
      classNames={['se-cancel-delete-button', PendoClassNames.ExpCancelDelete]}
      overlayClassName="se-dark-theme-tooltip"
    />
  );
  // Danger variant: cancel ("No") is the secondary button on the left, confirm ("Yes, Delete") is a
  // red primary button on the right. Default keeps the original confirm-left / cancel-right order.
  const dangerConfirmButton = (
    <PrimaryButton
      uniqueKey="se-confirm-delete-button"
      isDisable={false}
      displayName={confirmText}
      tooltipOverlay={<span>{confirmText}</span>}
      onClickHandler={() => onDeleteConfirmation()}
      classNames={['se-danger-confirm', PendoClassNames.ExpDeleteConfirmBtn]}
      overlayClassName="se-dark-theme-tooltip"
    />
  );
  const dangerCancelButton = (
    <SecondaryButton
      uniqueKey="se-cancel-delete-button"
      onClickHandler={() => onClose()}
      displayName={cancelText}
      classNames={['se-cancel-delete-button', PendoClassNames.ExpCancelDelete]}
      tooltip={cancelText}
      tooltipPlacement="bottom"
    />
  );
  const footerContent = (
    <div className="se-confirm-delete-button-container">
      {danger ? (
        <>
          {dangerCancelButton}
          {dangerConfirmButton}
        </>
      ) : (
        <>
          {confirmButton}
          {cancelButton}
        </>
      )}
    </div>
  );
  return (
    <StyledDeleteConfirmationModal>
      <Modal
        show={show}
        onClose={onClose}
        title={titleContent}
        footer={footerContent}
        classNames={[PendoClassNames.ExpDeleteCrossBtn]}
        closeOnBlurClick={false}
      >
        <div className="se-confirm-delete-body">{modalContent}</div>

        {optionalContent && (
          <div className="se-confirm-delete-sub-content">{optionalContent}</div>
        )}

        {confirmationQuestion && (
          <div className="se-confirm-delete-question">{confirmationQuestion}</div>
        )}
      </Modal>
    </StyledDeleteConfirmationModal>
  );
};
export default DeleteConfirmationModal;
